import { Container, Modal, TabItemProps, Tabs } from '@/components';
import { useAttachUserBusinessArea } from '@/modules/BusinessAreas/queries/attach-user-businessArea';
import { useCreateUser } from '@/modules/Users/queries/create-user';
import { useGetBusinessAreaUserProfiles } from '@/modules/Users/queries/get-business-area-user-profiles';
import { UserSchemaType } from '@/modules/Users/types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCsvTabModal } from '../AddCsvTab';
import { AssociateUserTab } from '../AssociateUserTab/AssociateUserTab';
import { UserModal } from '../UserModal';

type CreateBusinessAreaUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  businessAreaId: number;
};

export const CreateBusinessAreaUserModal = ({
  isOpen,
  onClose,
  businessAreaId,
}: CreateBusinessAreaUserModalProps) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const { data: roles } = useGetBusinessAreaUserProfiles();
  const { mutate: createUser, isLoading: IsLoadingCreateUser } =
    useCreateUser();

  const {
    mutate: attachUserBusinessArea,
    isLoading: isLoadingAttachUserBusinessArea,
  } = useAttachUserBusinessArea();

  const onSubmitUserAssociation = useCallback(
    (userProfiles: Array<{ userId: number; roleId: number }>) => {
      attachUserBusinessArea(
        {
          businessAreaId,
          userProfiles,
        },
        {
          onSuccess: () => {
            onClose();
          },
        },
      );
    },
    [attachUserBusinessArea, businessAreaId, onClose],
  );

  const onSubmitUserCreate = useCallback(
    async (user: UserSchemaType) => {
      if (!user.role) {
        return;
      }

      createUser(
        {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          status: user.status,
          access: user.access,
        },
        {
          onSuccess: ({ data: { userId } }) => {
            onSubmitUserAssociation([{ userId, roleId: user.role! }]);
          },
        },
      );
    },
    [createUser, onSubmitUserAssociation],
  );

  const tabs: Array<TabItemProps> = useMemo(
    () => [
      { label: t('create_new') },
      {
        label: t('add_through_csv'),
      },
      {
        label: t('add_existing_users'),
      },
    ],
    [t],
  );

  const modalTitle = selectedTab !== 2 ? 'create_users' : 'add_users';

  return (
    <Modal
      className="h-fit w-128"
      isOpen={isOpen}
      title={t(modalTitle)}
      handleClose={onClose}
    >
      <div className="mt-6 flex flex-col">
        <Container>
          <Tabs
            activeTab={selectedTab}
            tabs={tabs}
            onTabChange={setSelectedTab}
          />
        </Container>
        {selectedTab === 0 && (
          <UserModal
            isLoading={IsLoadingCreateUser || isLoadingAttachUserBusinessArea}
            handleOnSubmit={onSubmitUserCreate}
            handleClose={onClose}
            type="create"
            roles={roles}
            roleEntity="business-area"
          />
        )}
        {selectedTab === 1 && (
          <AddCsvTabModal
            businessAreaId={businessAreaId}
            handleClose={onClose}
          />
        )}
        {selectedTab === 2 && (
          <AssociateUserTab
            roles={roles}
            onClose={onClose}
            onSubmit={(items) =>
              onSubmitUserAssociation(
                items.map((item) => ({
                  userId: item.id,
                  roleId: item.role!,
                })),
              )
            }
            isSubmitting={isLoadingAttachUserBusinessArea}
            roleEntity="business-area"
          />
        )}
      </div>
    </Modal>
  );
};
